/**
 * @voltxampere/web — `<vxa-component-library>` (Sprint 11).
 *
 * Renders the four Sprint 10.1 categories (Sources, Passive,
 * Active, Connectors) as a stacked grid of `<vxa-symbol-card>`
 * elements. Re-runs the substring filter against the locale
 * names + canonical id whenever `sidebarSearchQuery` mutates.
 *
 * Empty state: when every category filters to zero, render the
 * `sidebar.empty` placeholder instead of an empty void.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { LitElement, html, css } from "lit";
import { customElement } from "lit/decorators.js";
import { type ComponentSymbol, type SymbolCategory, symbolsByCategory } from "@voltxampere/ui";

import { sidebarSearchQuery } from "../store/ui.js";
import { currentLocale } from "../store/locale.js";
import { t, type TranslationKey } from "../i18n/index.js";
import { SignalController } from "../lit-utils/signal-controller.js";

import "./vxa-symbol-card.js";

const SECTIONS: readonly (readonly [SymbolCategory, TranslationKey])[] = [
  ["source", "sidebar.section.sources"],
  ["passive", "sidebar.section.passive"],
  ["active", "sidebar.section.active"],
  ["connector", "sidebar.section.connectors"],
];

@customElement("vxa-component-library")
export class VxaComponentLibrary extends LitElement {
  private readonly _query = new SignalController(this, sidebarSearchQuery);
  private readonly _locale = new SignalController(this, currentLocale);

  static override styles = css`
    :host {
      display: block;
    }

    .section + .section {
      margin-top: var(--s-6);
    }

    .section-header {
      font-family: var(--font-mono);
      font-size: var(--fs-mono-tiny);
      letter-spacing: 0.28em;
      color: var(--brass);
      text-transform: uppercase;
      margin-bottom: var(--s-3);
      padding-bottom: var(--s-2);
      border-bottom: 1px solid var(--ivory-wisp);
    }

    /* Sprint 18 D17 — mockup zenit-platform.html'de palette
     * tek-sütun list halinde; 2-col grid'den 1-col'a geçiş
     * sembolün soluna alanı, ad/kısayolu sağa konumlandırır
     * (yatay kart layout — vxa-symbol-card.ts içinde). */
    .grid {
      display: grid;
      grid-template-columns: 1fr;
      gap: 2px;
      background: var(--ivory-wisp);
      border: 1px solid var(--ivory-wisp);
    }

    .empty {
      padding: var(--s-7) var(--s-3);
      text-align: center;
      font-family: var(--font-body);
      font-style: italic;
      font-size: var(--fs-small);
      color: var(--ivory-faint);
    }
  `;

  private filterSymbols(category: SymbolCategory): readonly ComponentSymbol[] {
    const q = this._query.value.toLowerCase().trim();
    const all = symbolsByCategory(category);
    if (q.length === 0) return all;
    return all.filter(
      (s) =>
        s.nameTr.toLowerCase().includes(q) ||
        s.nameEn.toLowerCase().includes(q) ||
        s.id.toLowerCase().includes(q),
    );
  }

  override render() {
    // NOTE on the array return: under happy-dom + Lit, an outer
    // template whose entire body is a single `${arr.map(...)}`
    // hole renders to a sentinel comment instead of unrolling the
    // array. Returning the array directly from render() is also a
    // valid Lit pattern and avoids the empty-host workaround.
    const populated = SECTIONS.map(([cat, key]) => ({
      cat,
      key,
      items: this.filterSymbols(cat),
    })).filter((s) => s.items.length > 0);

    if (populated.length === 0) {
      return html`<p class="empty">${t("sidebar.empty")}</p>`;
    }

    return populated.map(
      (s) => html`
        <div class="section" data-category=${s.cat}>
          <div class="section-header">${t(s.key)}</div>
          <div class="grid">
            ${s.items.map((sym) => html`<vxa-symbol-card .symbol=${sym}></vxa-symbol-card>`)}
          </div>
        </div>
      `,
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "vxa-component-library": VxaComponentLibrary;
  }
}
